import { useAppSelector } from 'hooks/redux';
import React from 'react';
import { useDispatch } from 'react-redux';

// DATA
import { aggErrors } from 'app-components/flux/account-discovery';
import { accountsUtils } from 'app-components/flux/accounts';
import { billerAccountsSelectors } from 'app-components/flux/biller-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransaction,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import type { transactionsTypes } from 'app-components/flux/transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import store from 'app-components/utils/redux-store';

import DownloadingIcon from '@mui/icons-material/DownloadingRounded';
import ErrorIcon from '@mui/icons-material/ErrorRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// CUSTOM
import UpdatedIcon from 'assets/icons/mui/status-updated.svg';
import QButton from 'components/QButton';

const useStyles = makeStyles<{ final: boolean; error: boolean }>()((theme, { final, error }) => ({
  billStatusRoot: {
    display: 'flex',
    alignItem: 'center',
    padding: '8px 16px',
    borderRadius: 12,
    marginTop: 8,
    backgroundColor: error ? theme.palette.error.light : final ? theme.palette.success.light : theme.palette.info.light,
  },
  icon: {
    height: 24,
    width: 24,
    marginRight: 8,
    color: error ? theme.palette.error.main : theme.palette.info.main,
  },
  button: {
    flexShrink: 0,
    marginLeft: 4,
  },
}));

interface BillStatusProps {
  txn?: transactionsTypes.TransactionProps;
  model?: ScheduledTransaction;
  onClose: () => void;
}

function BillStatus(props: BillStatusProps) {
  const dispatch = useDispatch();
  const { txn, model, onClose } = props;

  const connected = model?.billPresentmentAccountId && model?.billPresentmentAccountId !== '0';
  const billerAccount = useAppSelector(
    (state) => connected && billerAccountsSelectors.getBillerAccountForId(state, model?.billPresentmentAccountId),
  );

  const creditAccount = useAppSelector((state) => txn && transactionsSelectors.getCreditAccountFromTxn(state, txn));
  const institutionLogin = useAppSelector((state) =>
    creditAccount?.institutionLoginId
      ? institutionLoginsSelectors.getInstitutionLoginForId(state, creditAccount.institutionLoginId)
      : undefined,
  );

  const { autoAdjustAmount, autoAdjustDueOn } = model || {};
  const bvaEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state).get('billsViaAggregation'));

  const hasBVA = Boolean(
    bvaEnabled &&
      creditAccount &&
      transactionsUtils.isTransferTxn(txn) &&
      ((autoAdjustAmount && autoAdjustAmount !== AutoAdjustAmountEnum.none) || autoAdjustDueOn),
  );

  const error = accountsUtils.isAccountInError(creditAccount)?.isError;
  let final = Boolean(txn && (txn.ebillId === '1' || txn.ebillId === '2'));

  const { classes } = useStyles({ error, final });

  if (!hasBVA && !billerAccount) {
    return null;
  }

  let Icon = LinkIcon;
  let text = 'The amount and date of this reminder will be updated once your statement period begins.';
  if (error) {
    Icon = ErrorIcon;
    text = 'Linked account has been disconnected \nand reminder details can’t be updated.';
  } else if (txn?.ebillId === '3') {
    Icon = DownloadingIcon;
    text =
      'The statement period for this bill has begun, and the reminder amount will continue to update automatically.';
  } else if (final) {
    text = 'The amount and date of this reminder has been updated with your finalized bill information.';
  }

  if (!hasBVA) {
    Icon = LinkIcon;
    if (txn?.ebillId) {
      final = true;
      text = 'The amount and date of this reminder have been updated with your latest bill information.';
    } else {
      text = 'The amount and date of this reminder will be updated once your biller issues a new bill.';
    }
  }

  return (
    <div className={classes.billStatusRoot}>
      {final ? <img alt='status-icon' src={UpdatedIcon} className={classes.icon} /> : <Icon className={classes.icon} />}

      {/* <Icon className={classes.icon} /> */}

      <Typography variant='captionRegular'>{text}</Typography>

      {error ? (
        <QButton
          className={classes.button}
          variant='edit'
          id='bill-reconnect'
          onClick={() => {
            const aggregation = creditAccount?.aggregators?.get(0);
            const aggAction = aggErrors.getQcsAggregationAction(
              institutionLogin,
              aggregation?.aggStatus,
              aggregation?.aggStatusCode,
              store && featureFlagsSelectors.getFeatureFlags(store.getState()),
            );
            aggErrors.handleAggAction(dispatch, aggAction, 'BVA connection status');
            onClose();
          }}
        >
          Reconnect
        </QButton>
      ) : null}
    </div>
  );
}

BillStatus.displayName = 'BillStatus';

export default BillStatus;
